import { makeStyles } from 'tss-react/mui';
import React, { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomDropZone, DropZoneHelperText, getInitialFiles, Grid, Typography } from 'web-library';
import { UploadedFile } from 'web-library/models/UploadedFile';
import { attachedFilesService } from '../../../api';
import { ResourceFileConfigResponse } from '../../../api-refactored/onlineTutorials/useGetResourceFileConfig';
import { UploadDocumentType } from '../../../api/candidate/config/model';
import AttachedFiles from '../../../components/upload-documents/AttachedFiles';
import { useUserDetails } from '../../../hooks/userDetails';
import { Role } from '../../../models';
import { handleFileDelete } from '../../../pages/accessArrangement/SelectCondition/components/UploadDocuments/components/useUploadFile';
import { convertMB2Byte } from '../../../utils/FileUtils';
import { FormIds } from '../../withdrawal/logic/useUploadDocument';

const useStyles = makeStyles<{ files:UploadedFile[], draftFiles?: UploadedFile[] }>()((theme, { files, draftFiles }) =>
({
  header: {
    marginBottom: theme.spacing(2),
  },
  description: {
    marginBottom: theme.spacing(4),
  },
  passportDropzone: {
    '& .MuiDropzoneArea-text': {
      color: files.length === 1 || (draftFiles && draftFiles.length === 1)
          ? theme.color.text.textDisabled
          : '',
    },
    borderColor: files.length === 1 || (draftFiles && draftFiles.length === 1)
        ? theme.color.border.borderFocused
        : '',
  },
  disabledDropzone: {
    pointerEvents: files.length === 1 || draftFiles?.length === 1 ? 'none' : 'auto',
    caretColor: 'transparent',
  },
  errString: {
    color: theme.color.state.stateError,
  },
}));

interface UploadPassportPhotoProps {
  files: UploadedFile[];
  setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  hasError?: boolean;
  isHeader?: boolean;
  submittedFiles?: number;
  draftFiles?: UploadedFile[];
  setDraftFiles?: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  uploadDocumentType: UploadDocumentType;
  isLoading?: boolean;
  setFileErrorMessage: Dispatch<SetStateAction<string>>;
  resourceFileConfig: ResourceFileConfigResponse | undefined;
}

const onFileUpload = (
  uploadedFiles: File[],
  setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>
) => {
  setFiles(
    uploadedFiles.map((file) => ({
      file,
      uploaded: false,
      hasError: false,
      pending: false,
    }))
  );
};

const UploadDocumentTutorials = ({
  files,
  setFiles,
  hasError,
  isHeader,
  submittedFiles,
  draftFiles,
  setDraftFiles,
  uploadDocumentType,
  isLoading,
  resourceFileConfig,
  setFileErrorMessage,
}: UploadPassportPhotoProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles({ files, draftFiles });
  const [dropzoneKey, setDropzoneKey] = useState<string>('');

  const { data: userDetails } = useUserDetails();
  const { mutate: deleteFileMutate } = attachedFilesService.all.useDeleteAppealArrangementFile();

  const onUploadFileDelete = async (file: File, isDraft = false) => {
    // to handle file delete
    if (isDraft && draftFiles && setDraftFiles) {
      handleFileDelete(file, draftFiles, setDraftFiles, deleteFileMutate);
    } else {
      handleFileDelete(file, files, setFiles, deleteFileMutate);
    }

    setDropzoneKey(Date.now().toString());
  };

  const initialFiles = getInitialFiles(files);

  return (
    <>
      {isHeader && (
        <>
          <Typography
            variant="h3"
            id={FormIds.UPLOAD_DOCUMENTS_CONTAINER}
            className={`${classes.header} ${hasError ? classes.errString : ''}`}
          >
            {t('accessArrangement->>SelectCondition.UploadDocumentsHeader')}
          </Typography>
          <Typography
            variant="body1"
            className={`${classes.description} ${hasError ? classes.errString : ''}`}
          >
            {t('accessArrangement->>SelectCondition.UploadDocumentsDescription')}
          </Typography>
        </>
      )}
      {resourceFileConfig && (
        <Grid xs={12} sm={12} md={12} lg={12}>
          <CustomDropZone
            filesLimit={resourceFileConfig.fileNumLimit}
            acceptedFiles={resourceFileConfig.uploadFileTypes}
            maxSize={convertMB2Byte(resourceFileConfig.fileSizeLimit)}
            className={`${classes.passportDropzone} ${classes.disabledDropzone}`}
            key={dropzoneKey}
            initialFiles={initialFiles}
            onChange={(f) => onFileUpload(f, setFiles)}
            onDelete={onUploadFileDelete}
          />
          <DropZoneHelperText
            acceptedFileTypes={resourceFileConfig.uploadFileTypes}
            fileLimit={resourceFileConfig.fileNumLimit}
            fileSizeLimit={resourceFileConfig.fileSizeLimit}
          />
        </Grid>
      )}

      <AttachedFiles
        files={files}
        setFiles={setFiles}
        onUploadFileDelete={onUploadFileDelete}
        draftFiles={draftFiles}
        downloadableLink={userDetails?.data.roles?.includes(Role.opsAdmin)}
        isLoading={isLoading}
      />
    </>
  );
};
export default UploadDocumentTutorials;
